import { useState } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../../context/useAuth';
import { useOrg } from '../../../context/OrgContext';

const breadcrumbMap = {
  '/admin/dashboard': 'Dashboard',
  '/admin/courses': 'Courses',
  '/admin/content': 'Manage Static Content',
  '/admin/instructors': 'Instructors & Mentors',
  '/admin/users': 'Users & Roles',
  '/admin/organization': 'Organization',
  '/admin/billing': 'Billing',
  '/admin/analytics': 'Analytics',
};

function getBreadcrumb(pathname) {
  const crumbs = [{ label: 'Admin', to: '/admin/dashboard' }];
  if (pathname.includes('/admin/courses/new')) {
    crumbs.push({ label: 'Courses', to: '/admin/courses' });
    crumbs.push({ label: 'New Course', to: '/admin/courses/new' });
  } else if (pathname.match(/\/admin\/courses\/.+\/edit/)) {
    crumbs.push({ label: 'Courses', to: '/admin/courses' });
    crumbs.push({ label: 'Edit Course', to: pathname });
  } else if (pathname === '/admin/content') {
    crumbs.push({ label: 'Manage Static Content', to: '/admin/content' });
  } else if (breadcrumbMap[pathname]) {
    crumbs.push({ label: breadcrumbMap[pathname], to: pathname });
  }
  return crumbs;
}

function getPageTitle(pathname) {
  if (pathname === '/admin/dashboard') return 'Dashboard';
  if (pathname === '/admin/courses') return 'Manage Courses';
  if (pathname === '/admin/content') return 'Manage Static Content (CMS)';
  if (pathname.includes('/admin/courses/new')) return 'Create New Course';
  if (pathname.match(/\/admin\/courses\/.+\/edit/)) return 'Edit Course';
  if (pathname === '/admin/instructors') return 'Instructors Management';
  if (pathname === '/admin/users') return 'User & Access Directory';
  if (pathname === '/admin/organization') return 'Organization';
  if (pathname === '/admin/billing') return 'Billing & Plan';
  if (pathname === '/admin/analytics') return 'Analytics & Reporting';
  return 'Admin Portal';
}

export default function AdminLayout() {
  const { user, token, loading, logout } = useAuth();
  const { organizations, currentOrgId, currentOrg, setCurrentOrgId } = useOrg();
  const location = useLocation();
  const [copiedLink, setCopiedLink] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);

  // Authentication check
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-6">
        <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-slate-300 text-xs">Loading admin workspace...</p>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  const role = (user?.role || '').toLowerCase();
  const isAuthorized = role === 'admin' || role === 'superadmin' || role === 'instructor';

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 px-6 py-12">
        <div className="max-w-md w-full bg-slate-950 border border-slate-800 rounded-3xl p-8 text-center shadow-2xl">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4 text-amber-400 text-2xl">
            <i className="ri-shield-keyhole-line" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400 text-xs mb-6 leading-relaxed">
            Your account ({user?.email}) is registered as a <strong className="text-teal-400 capitalize">{role || 'Student'}</strong>. Only Administrators and Instructors can access this area.
          </p>
          <div className="space-y-3">
            <Link
              to="/dashboard"
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs transition-all shadow-md cursor-pointer"
            >
              <i className="ri-arrow-left-line" /> Go to Student Dashboard
            </Link>
            <button
              type="button"
              onClick={() => {
                logout();
                window.location.href = '/login';
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-xs transition-all cursor-pointer border border-slate-700"
            >
              <i className="ri-logout-box-line" /> Sign in with Administrator Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  const navSections = [
    {
      label: 'Overview',
      links: [
        { to: '/admin/dashboard', icon: 'ri-dashboard-3-line', label: 'Dashboard' },
        // Any authenticated instructor/admin can own or belong to an
        // organization, so this link is available to every role that can
        // reach /admin -- not gated to the admin-only sections below.
        { to: '/admin/organization', icon: 'ri-building-2-line', label: 'Organization' },
        { to: '/admin/analytics', icon: 'ri-bar-chart-grouped-line', label: 'Analytics' },
        { to: '/admin/billing', icon: 'ri-bank-card-2-line', label: 'Billing & Plan' },
      ],
    },
    {
      label: 'Content Management',
      links: [
        { to: '/admin/courses', icon: 'ri-book-3-line', label: 'Courses' },
        ...(role === 'admin' || role === 'superadmin' ? [{ to: '/admin/content', icon: 'ri-layout-masonry-line', label: 'Static Content (CMS)' }] : []),
      ],
    },
    // Instructors don't get a "Faculty & Access" section at all -- managing
    // other instructors and account roles is an admin-only capability, not
    // something any one instructor should see or be able to reach.
    ...(role === 'admin' || role === 'superadmin' ? [{
      label: 'Faculty & Access',
      links: [
        { to: '/admin/instructors', icon: 'ri-team-line', label: 'Instructors' },
        { to: '/admin/users', icon: 'ri-user-settings-line', label: 'Users & Admins' },
      ],
    }] : []),
  ];

  const originUrl = typeof window !== 'undefined' ? window.location.origin : 'https://learnhub.app';
  const instructorInviteUrl = `${originUrl}/signup?role=instructor&ref=${user?.referralCode || 'INST'}`;

  const copyQuickInvite = () => {
    navigator.clipboard.writeText(instructorInviteUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const breadcrumbs = getBreadcrumb(location.pathname);
  const pageTitle = getPageTitle(location.pathname);
  const displayName = user?.userName || (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email?.split('@')[0] || 'User');

  return (
    <div className="flex min-h-screen bg-slate-100 relative max-w-full overflow-x-hidden">
      {/* Mobile Drawer Backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-40 lg:hidden transition-opacity"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar (Responsive Drawer on mobile, Fixed Sidebar on desktop) */}
      <aside 
        className={`w-64 flex-shrink-0 fixed top-0 left-0 bottom-0 z-50 flex flex-col bg-slate-900 border-r border-slate-800 transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Brand & Mobile Close Button */}
        <div className="px-6 pt-6 pb-5 border-b border-slate-800/80 flex items-center justify-between">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2.5"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div className="w-9 h-9 rounded-xl bg-teal-500 flex items-center justify-center text-white text-lg font-bold shadow-md">
              <i className="ri-graduation-cap-fill" />
            </div>
            <span className="text-xl font-bold text-white tracking-wide">LearnHub</span>
          </Link>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            title="Close sidebar"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-slate-800/40">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full ${
                role === 'superadmin'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-400/30 shadow-xs'
                  : role === 'admin'
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-400/30'
                  : 'bg-teal-500/20 text-teal-300 border border-teal-400/30'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                  role === 'superadmin' ? 'bg-amber-400' : role === 'admin' ? 'bg-purple-400' : 'bg-teal-400'
                }`}
              />
              {role === 'superadmin' ? '👑 Super Admin' : role === 'admin' ? 'Admin Portal' : 'Instructor Portal'}
            </span>
          </div>
        </div>

        {/* Organization Switcher -- scopes Dashboard + Courses to one org,
            or "Personal" (own courses, or every course for admin/superadmin) */}
        <div className="px-4 py-3 border-b border-slate-800/40 relative">
          <button
            type="button"
            onClick={() => setOrgMenuOpen((v) => !v)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-800 border border-slate-700/50 transition-colors cursor-pointer"
          >
            <div className="w-7 h-7 rounded-lg bg-slate-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
              {currentOrg?.logoUrl ? (
                <img src={currentOrg.logoUrl} alt={currentOrg.name} className="w-full h-full object-cover" />
              ) : currentOrg ? (
                currentOrg.name.charAt(0).toUpperCase()
              ) : (
                <i className="ri-user-line" />
              )}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-[10px] text-slate-500 leading-none mb-0.5">Viewing</p>
              <p className="text-xs font-bold text-white truncate">{currentOrg ? currentOrg.name : 'Personal'}</p>
            </div>
            <i className={`ri-arrow-${orgMenuOpen ? 'up' : 'down'}-s-line text-slate-400 flex-shrink-0`} />
          </button>

          {orgMenuOpen && (
            <div className="absolute left-4 right-4 top-full mt-1.5 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden py-1">
              <button
                type="button"
                onClick={() => { setCurrentOrgId(null); setOrgMenuOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs font-semibold text-left transition-colors cursor-pointer ${
                  !currentOrgId ? 'bg-teal-600/20 text-teal-300' : 'text-slate-300 hover:bg-slate-800'
                }`}
              >
                <i className="ri-user-line" /> Personal
                {!currentOrgId && <i className="ri-check-line ml-auto" />}
              </button>
              {organizations.length > 0 && <div className="h-px bg-slate-800 my-1" />}
              {organizations.map((org) => (
                <button
                  key={org._id}
                  type="button"
                  onClick={() => { setCurrentOrgId(org._id); setOrgMenuOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs font-semibold text-left transition-colors cursor-pointer ${
                    currentOrgId === org._id ? 'bg-teal-600/20 text-teal-300' : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <span className="w-5 h-5 rounded-md bg-slate-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden">
                    {org.logoUrl ? (
                      <img src={org.logoUrl} alt={org.name} className="w-full h-full object-cover" />
                    ) : (
                      org.name.charAt(0).toUpperCase()
                    )}
                  </span>
                  <span className="truncate">{org.name}</span>
                  {currentOrgId === org._id && <i className="ri-check-line ml-auto flex-shrink-0" />}
                </button>
              ))}
              <div className="h-px bg-slate-800 my-1" />
              <Link
                to="/admin/organization"
                onClick={() => { setOrgMenuOpen(false); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-semibold text-left text-teal-400 hover:bg-slate-800 transition-colors cursor-pointer"
              >
                <i className="ri-add-circle-line" />
                {organizations.length > 0 ? 'Manage organizations' : 'Create an organization'}
              </Link>
            </div>
          )}
        </div>

        {/* Nav Links */}
        <nav className="flex-1 px-4 py-5 overflow-y-auto space-y-6">
          {navSections.map((section) => (
            <div key={section.label}>
              <p className="text-[10px] uppercase font-bold tracking-widest px-3 mb-2 text-slate-500">
                {section.label}
              </p>
              <div className="space-y-1">
                {section.links.map((link) => {
                  const isActive =
                    link.to === '/admin/dashboard'
                      ? location.pathname === '/admin/dashboard'
                      : location.pathname.startsWith(link.to);
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                        isActive
                          ? 'bg-teal-600 text-white shadow-md'
                          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                      }`}
                    >
                      <i className={`${link.icon} text-base`} />
                      <span className="flex-1">{link.label}</span>
                      {isActive && (
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-200" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User Card & Signout */}
        <div className="p-4 border-t border-slate-800 bg-slate-950/40">
          <div className="flex items-center gap-3 p-2.5 rounded-xl mb-3 bg-slate-800/60 border border-slate-700/50">
            <div className="w-9 h-9 rounded-xl bg-teal-600 text-white font-bold flex items-center justify-center text-xs flex-shrink-0">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white font-bold truncate">{displayName}</p>
              <p className="text-[10px] text-slate-400 capitalize truncate">{role}</p>
            </div>
          </div>

          <div className="flex gap-2">
            <Link
              to="/dashboard"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl text-[11px] font-semibold bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
            >
              <i className="ri-eye-line text-xs" /> Student View
            </Link>
            <button
              type="button"
              onClick={logout}
              className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl text-[11px] font-semibold bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/20 transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-line text-xs" /> Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 lg:ml-64 flex flex-col min-h-screen max-w-full overflow-x-hidden">
        {/* Sticky Header */}
        <header className="sticky top-0 z-30 flex items-center justify-between px-4 sm:px-8 py-3.5 sm:py-4 bg-white/90 backdrop-blur-md border-b border-slate-200/80">
          <div className="flex items-center gap-3 min-w-0">
            {/* Hamburger Toggle Button for Mobile */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors flex-shrink-0"
              title="Open menu"
            >
              <i className="ri-menu-2-line text-xl" />
            </button>

            <div className="min-w-0">
              <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-0.5 truncate">
                {breadcrumbs.map((crumb, i) => (
                  <span key={crumb.to} className="flex items-center gap-1.5">
                    {i > 0 && <i className="ri-arrow-right-s-line text-slate-300" />}
                    {i < breadcrumbs.length - 1 ? (
                      <Link to={crumb.to} className="hover:text-teal-600 transition-colors">
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="font-semibold text-slate-800 truncate">{crumb.label}</span>
                    )}
                  </span>
                ))}
              </nav>
              <h1 className="text-base sm:text-lg font-bold text-slate-900 truncate">{pageTitle}</h1>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {(role === 'admin' || role === 'superadmin') && (
              <Link
                to="/admin/content"
                className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-indigo-50 hover:bg-indigo-100 text-indigo-700 transition-all cursor-pointer border border-indigo-200/70 shadow-2xs"
                title="Manage static home page and website content"
              >
                <i className="ri-layout-masonry-line text-indigo-600" />
                <span>Static CMS</span>
              </Link>
            )}

            <button
              type="button"
              onClick={copyQuickInvite}
              className="hidden sm:flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-all cursor-pointer border border-slate-200 shadow-2xs"
            >
              <i className={copiedLink ? 'ri-check-line text-emerald-600' : 'ri-link-m text-teal-600'} />
              {copiedLink ? 'Copied!' : 'Copy Invite'}
            </button>

            <Link
              to="/admin/courses/new"
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 shadow-sm transition-all cursor-pointer"
            >
              <i className="ri-add-line text-sm" />
              <span className="hidden xs:inline">New Course</span>
              <span className="xs:hidden">New</span>
            </Link>
          </div>
        </header>

        {/* Content Body */}
        <main className="flex-1 p-4 sm:p-6 md:p-8 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
